import type { CalendarBlockLike } from '@/components/calendar/block-bars'
import { holdOpenValue, ownerBlockOpenValue } from './block-utils'

/**
 * What a calendar page's URL says is open, and how to open something else:
 * shared by the property Calendar tab and the portfolio calendar (CAP-1418),
 * which put the same values in different parameters.
 */

// What an open value can be: `stay:<reservation id>`, `cleaning:<job id>`,
// `supply:<category>:<YYYY-MM-DD>` (see `supplyOpenValue`), `gap:<first night>`
// (see `gapOpenValue`), `day:<YYYY-MM-DD>` (the day list, see `dayOpenValue`),
// or a block's (see `block-utils.ts`). A job id has colons of its own
// (`reservation:11162333`), so only the first prefix is stripped.
const STAY_OPEN_PREFIX = 'stay:'
const CLEANING_OPEN_PREFIX = 'cleaning:'

export function stayOpenValue(reservationId: string): string {
  return `${STAY_OPEN_PREFIX}${reservationId}`
}

export function cleaningOpenValue(jobId: string): string {
  return `${CLEANING_OPEN_PREFIX}${jobId}`
}

export function openId(raw: string | null, prefix: string): string | null {
  return raw?.startsWith(prefix) ? raw.slice(prefix.length) || null : null
}

export function parseStayOpen(raw: string | null): string | null {
  return openId(raw, STAY_OPEN_PREFIX)
}

export function parseCleaningOpen(raw: string | null): string | null {
  return openId(raw, CLEANING_OPEN_PREFIX)
}

export interface SheetNavOptions {
  /** Replace the history entry rather than add one (closing a sheet). */
  replace?: boolean
  /** The day list a block's sheet is opened from, so the sheet can go back to it. */
  fromDay?: string
  /** Where in a stay's sheet to scroll to (`DEPOSIT_SECTION`, `REVIEW_SECTION`). */
  section?: string
}

/** A page's URL state for its open sheet, and the one way to change it. */
export interface CalendarSheetNav {
  /** What is open, or null. */
  open: string | null
  /** The part of the stay's sheet to scroll to; means nothing without a stay open. */
  section: string | null
  /** The day list the open block was reached from (`YYYY-MM-DD`). */
  fromDay: string | null
  /** Opens `value`, or with null closes the sheet. */
  go: (value: string | null, options?: SheetNavOptions) => void
}

/** Opens a block's or hold's sheet; `fromDay` is the day list it is opened from, so its sheet can go back. */
export function openBlockSheet(
  go: CalendarSheetNav['go'],
  block: CalendarBlockLike,
  fromDay?: string
) {
  go(block.kind === 'owner' ? ownerBlockOpenValue(block.id) : holdOpenValue(block.id), { fromDay })
}
